import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../properties/PropertyMap.css";

/** Interactive crew map: numbered on-site pins (blue in-house / green supplier / red emergency) plus pulsing dots
 *  for engineers sharing a live phone position. No API key needed (OpenStreetMap tiles). */
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const initials = (name) => (name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const pinIcon = (o, idx) => L.divIcon({
  className: "",
  html: `<div class="fx-crew-pin fx-crew-${o.is_emergency ? "red" : o.team === "supplier" ? "green" : "blue"}" title="${esc(o.operative_name)}">${idx}</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14],
});
const liveIcon = (s) => L.divIcon({
  className: "",
  html: `<div class="fx-live ${s.fresh ? "fx-live-fresh" : "fx-live-stale"}" title="${esc(s.operative_name)}"><span class="fx-live-ring"></span><span class="fx-live-dot">${esc(initials(s.operative_name))}</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -14],
});

function Fit({ points, fitKey }) {
  const map = useMap();
  // re-fit only when who/where changes, not on every 60 s poll — keeps the user's pan/zoom
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(L.latLngBounds(points).pad(0.2), { maxZoom: 14 });
  }, [map, fitKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LiveTeamLeafletMap({ onsite = [], sharing = [], selected, onSelect, filtered = false }) {
  const pins = onsite.map((o, i) => ({ o, idx: o._idx || i + 1 })).filter(({ o }) => o.latitude != null && o.longitude != null); // numbers match the side list
  const points = [...pins.map(({ o }) => [o.latitude, o.longitude]), ...sharing.map((s) => [s.latitude, s.longitude])];
  const fitKey = useMemo(() => [...pins.map(({ o }) => `${o.operative_id}@${o.latitude?.toFixed(3)},${o.longitude?.toFixed(3)}`), ...sharing.map((s) => `${s.operative_id}@${s.latitude.toFixed(3)},${s.longitude.toFixed(3)}`)].join("|"), [pins, sharing]);
  if (!points.length) {
    return (
      <div className="aspect-[2/1] flex items-center justify-center text-sm text-slate-500" data-testid="live-map-no-coords">
        {filtered ? "No one in this team is on site or sharing a position right now." : "Map unavailable — no coordinates captured yet."}
      </div>
    );
  }
  return (
    <div className="aspect-[2/1] property-map" data-testid="live-map-leaflet">
      <MapContainer center={[54.2, -2.5]} zoom={6} scrollWheelZoom keyboard={false} className="h-full w-full">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Fit points={points} fitKey={fitKey} />
        {sharing.map((s) => (
          <Marker key={`live-${s.operative_id}`} position={[s.latitude, s.longitude]} icon={liveIcon(s)} keyboard={false} zIndexOffset={s.fresh ? 500 : 0}>
            <Popup>
              <div className="text-xs space-y-0.5" data-testid={`live-map-live-popup-${s.operative_id}`}>
                <p className="font-semibold text-sm">{s.operative_name}</p>
                <p>{s.fresh ? "Live" : "Last shared"} · {s.age_minutes != null ? (s.age_minutes ? `${s.age_minutes} min ago` : "just now") : ""}{s.accuracy_m ? ` · ±${s.accuracy_m} m` : ""}</p>
                <p className="text-muted-foreground">{s.team === "supplier" ? "Supplier engineer" : "In-house"}{s.on_site ? " · on site" : ""}</p>
              </div>
            </Popup>
          </Marker>
        ))}
        {pins.map(({ o, idx }) => (
          <Marker key={`${o.entity_id}-${o.operative_id}`} position={[o.latitude, o.longitude]} icon={pinIcon(o, idx)} keyboard={false}
            eventHandlers={{ click: () => onSelect && onSelect(o) }} zIndexOffset={selected && selected.entity_id === o.entity_id && selected.operative_id === o.operative_id ? 1000 : 100}>
            <Popup>
              <div className="text-xs space-y-0.5" data-testid={`live-map-pin-popup-${o.entity_id}-${o.operative_id}`}>
                <p className="font-semibold text-sm">{o.operative_name}</p>
                <p>{o.reference} · {o.title}</p>
                <p className="text-muted-foreground">{o.property_address}</p>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
